import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  EditContributionRefundRequest,
  EditContributionRefundResponse,
  LinkRefundToParentRequest,
  OrphanStandaloneRefundRequest,
  RefundMutationResponse,
  UncheckRefundRequest,
  RowVersion,
} from '@signalvote/shared';
import { apiJson, apiResponse } from './apiFetch';
import { queryKeys } from '../../lib/queryKeys';
import { throwStagedRowError } from './useFinanceContributions';
import {
  optimisticallyPatchSortInboxRow,
  rollbackSortInbox,
  reconcileSortInboxRowVersion,
  markRefundLinked,
  markRefundStandalone,
  markRefundOrphan,
  rowMutationScope,
  type LedgerMutationOptions,
} from './sortInboxOptimistic';
import { transientRetry } from './ledgerActionRetry';

export type { LedgerMutationOptions };

// USI-011 (ADR-011 § Structural commitment #4; design doc § Key decisions #3):
// orphan-refund pick-parent / skip-to-standalone mutations. The triage
// modal's OrphanRefundBody is the first frontend consumer.
//
// The backend service `IContributionRefundLinkService` enforces:
//   - Refund tenant filter → 404 RefundNotFound.
//   - Refund.ContributionId != null → 409 AlreadyLinked.
//   - Concurrency token mismatch → 409 ConcurrencyConflict.
//   - Original contribution tenant filter (link only) → 404
//     OriginalContributionNotFound.
//
// On success the endpoints return 200 OK carrying a `RefundMutationResponse` with the
// refund's freshly-bumped `concurrencyVersion` (the Layer 0 token the uncheck hooks write
// back — see below) plus the ADR-019 federal-gated `advisory` (null outside a calendar-locked
// FEDERAL period; record the correction as an FEC-portal amendment, alert-don't-block). Each
// hook parses that body and returns it, so a caller can surface the nudge. The FE always
// invalidates `sortInbox` to drop the row from the queue.

/**
 * USI-011 — typed error class for {@link useLinkRefundToParent} and
 * {@link useOrphanRefundStandalone}.
 *
 * Distinct shape from `ContributionConfirmError` / `DonorInfoRequestedError`
 * — the orphan-refund endpoints emit their own `ContributionRefundLinkErrorCodes`
 * namespace (`RefundNotFound` / `OriginalContributionNotFound` /
 * `ConcurrencyConflict` / `AlreadyLinked`). Surfacing as a typed class keeps
 * the body's 409 messaging structurally distinct: "already linked to a
 * parent contribution" reads very differently from "this row was modified
 * elsewhere."
 */
export class ContributionRefundLinkError extends Error {
  code: string;
  status: number;
  constructor(code: string, status: number, message: string) {
    super(message);
    this.name = 'ContributionRefundLinkError';
    this.code = code;
    this.status = status;
  }
}

/**
 * Parse a refund link-service mutation's 200 OK success body. All four actions now return
 * 200 + `RefundMutationResponse` carrying the refund's freshly-bumped `concurrencyVersion`
 * (the Layer 0 token every uncheck hook writes back) plus the ADR-019 federal-gated advisory
 * (null outside a calendar-locked federal period). Return type is `Partial<…>` on purpose: if
 * the body is somehow absent `concurrencyVersion` stays honestly `undefined` (not a lied-about
 * `number`), so the writeback becomes a `reconcileSortInboxRowVersion` no-op rather than
 * corrupting the cache with a bad value.
 */
async function readRefundMutationResponse(
  response: Response,
): Promise<Partial<RefundMutationResponse>> {
  const body = (await response.json().catch(() => null)) as Partial<RefundMutationResponse> | null;
  return {
    concurrencyVersion: body?.concurrencyVersion,
    advisory: body?.advisory ?? null,
  };
}

/**
 * POST /api/finance/contribution-refunds/{id}/link — link an orphan refund
 * to a chosen parent contribution. The backend writes one
 * `RefundLinkedToParent` audit row and bumps the refund's concurrency
 * version; on success the FE invalidates the sort-inbox so the row drops
 * from the queue.
 */
export function useLinkRefundToParent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      refundId,
      originalContributionId,
      concurrencyVersion,
      suggestedOriginalIds,
    }: {
      refundId: string;
      originalContributionId: string;
      concurrencyVersion: RowVersion;
      /**
       * USI-011: candidate set the FE displayed at choice time. Rides into
       * the `RefundLinkedToParent` audit row so the §17-5-8 trail records
       * the candidates the treasurer rejected before picking — distinguishes
       * a deliberate link from a blind one per ADR-011 § Negative
       * consequences.
       */
      suggestedOriginalIds?: string[];
    }) => {
      const response = await apiResponse(`/api/finance/contribution-refunds/${refundId}/link`, {
        method: 'POST',
        json: {
          originalContributionId,
          concurrencyVersion,
          suggestedOriginalIds: suggestedOriginalIds ?? [],
        } satisfies LinkRefundToParentRequest,
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new ContributionRefundLinkError(
          body.code ?? '',
          response.status,
          body.error ?? 'Failed to link refund to parent',
        );
      }
      return readRefundMutationResponse(response);
    },
    // Resolve the orphan row instantly (NeedsAction/MatchRefund → Done/Linked)
    // so the treasurer knows the link took; onSuccess pulls the fresh version.
    ...transientRetry,
    onMutate: async ({ refundId }) => {
      const previous = await optimisticallyPatchSortInboxRow(
        queryClient,
        (item) => item.kind === 'Refund' && item.refund.id === refundId,
        markRefundLinked,
      );
      return { previous };
    },
    onError: (_err, _variables, context) => {
      rollbackSortInbox(queryClient, context?.previous);
    },
    onSuccess: () => {
      // Sort-inbox refetch — symmetric with the other per-kind mutations
      // (useConfirmContribution / useDonorInfoRequested / useVoidContribution).
      // The next refresh drops the row from the orphan-refund queue because
      // the refund's `ContributionId` is no longer null.
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.sortInboxAll() });
    },
  });
}

/**
 * POST /api/finance/contribution-refunds/{id}/orphan-standalone — record the
 * treasurer's deliberate choice to leave the refund as a standalone
 * negative-amount row (Shape A: ContributionId stays null). The backend
 * writes one `RefundOrphanStandaloneChosen` audit row carrying
 * `chosenStandalone: true` + the optional reason; this is the §17-5-8
 * evidence the audit trail relies on post-ADR-011.
 */
export function useOrphanRefundStandalone() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      refundId,
      concurrencyVersion,
      reason,
      suggestedOriginalIds,
    }: {
      refundId: string;
      concurrencyVersion: RowVersion;
      reason?: string;
      /**
       * USI-011: rejected candidates ride into the
       * `RefundOrphanStandaloneChosen` audit row so the §17-5-8 trail
       * records WHICH parents the treasurer turned down before choosing
       * standalone. Empty `[]` is fine (no candidates were ever offered).
       */
      suggestedOriginalIds?: string[];
    }) => {
      const trimmed = reason && reason.trim() !== '' ? reason.trim() : null;
      const body: OrphanStandaloneRefundRequest = {
        concurrencyVersion,
        reason: trimmed,
        suggestedOriginalIds: suggestedOriginalIds ?? [],
      };
      const response = await apiResponse(
        `/api/finance/contribution-refunds/${refundId}/orphan-standalone`,
        { method: 'POST', json: body },
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new ContributionRefundLinkError(
          body.code ?? '',
          response.status,
          body.error ?? 'Failed to record orphan standalone choice',
        );
      }
      return readRefundMutationResponse(response);
    },
    // Resolve the orphan row instantly (NeedsAction/MatchRefund → Done/Standalone).
    ...transientRetry,
    onMutate: async ({ refundId }) => {
      const previous = await optimisticallyPatchSortInboxRow(
        queryClient,
        (item) => item.kind === 'Refund' && item.refund.id === refundId,
        markRefundStandalone,
      );
      return { previous };
    },
    onError: (_err, _variables, context) => {
      rollbackSortInbox(queryClient, context?.previous);
    },
    onSuccess: () => {
      // Skip-to-standalone keeps ContributionId = null (Shape A), but the
      // RefundOrphanStandaloneChosen audit row now drops the refund from the
      // sort-inbox aggregator permanently (USI-014 audit-history suppression).
      // Refetching the inbox is what makes the resolved row disappear for
      // every caller, not just the modal's session queue.
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.sortInboxAll() });
    },
  });
}

/**
 * Ledger uncheck (2026-06-12) — POST /contribution-refunds/{id}/unlink, the
 * inverse of `useLinkRefundToParent`. Clicking a Done/Linked refund's ✓ on
 * the All-Time Ledger instantly severs the parent link so the row returns
 * to the orphan (Match/Standalone) state.
 */
export function useUnlinkRefund(options?: LedgerMutationOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    // Serialize with the same row's revoke so a rapid uncheck → re-toggle queues instead of
    // racing a stale version — see rowMutationScope. Paired with the Layer 0 onSuccess
    // writeback below (the endpoint now returns the refund's freshly-bumped concurrencyVersion),
    // this is the full self-race fix: serialization queues the follow-up until the current
    // settles, and the writeback is what delivers the fresh token during rapid interaction (the
    // next action's onMutate cancelQueries kills the onSettled refetch, so it can't be the
    // source).
    scope: rowMutationScope(options?.scopeId),
    mutationFn: async ({
      refundId,
      concurrencyVersion,
    }: {
      refundId: string;
      concurrencyVersion: RowVersion;
    }) => {
      const response = await apiResponse(
        `/api/finance/contribution-refunds/${refundId}/unlink`,
        { method: 'POST', json: { concurrencyVersion } satisfies UncheckRefundRequest },
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new ContributionRefundLinkError(
          body.code ?? '',
          response.status,
          body.error ?? 'Failed to uncheck refund',
        );
      }
      return readRefundMutationResponse(response);
    },
    // Uncheck instantly (Done/Linked → NeedsAction/MatchRefund), back to orphan.
    ...transientRetry,
    onMutate: async ({ refundId }) => {
      const previous = await optimisticallyPatchSortInboxRow(
        queryClient,
        (item) => item.kind === 'Refund' && item.refund.id === refundId,
        markRefundOrphan,
      );
      return { previous };
    },
    onError: (_err, _variables, context) => {
      rollbackSortInbox(queryClient, context?.previous);
    },
    // Layer 0 — write the server's freshly-bumped token into the cached Refund row NOW, so a
    // rapid re-toggle (unlink → immediate revoke/re-link) sends the current version, not the
    // stale one it unlinked with. The onSettled refetch can't be the source during rapid
    // interaction: the next action's onMutate cancelQueries kills it.
    onSuccess: (data, { refundId }) => {
      reconcileSortInboxRowVersion(
        queryClient,
        (item) => item.kind === 'Refund' && item.refund.id === refundId,
        data.concurrencyVersion,
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.sortInboxAll() });
    },
  });
}

/**
 * Ledger uncheck (2026-06-12) — POST /contribution-refunds/{id}/revoke-standalone,
 * the inverse of `useOrphanRefundStandalone`. The backend appends a
 * `RefundOrphanStandaloneRevoked` audit row (the Chosen row is never deleted;
 * the sort-inbox Done signal is latest-event-wins), returning the refund to
 * the orphan (Match/Standalone) state.
 */
export function useRevokeRefundStandalone(options?: LedgerMutationOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    // Serialize with the same row's unlink so a rapid uncheck → re-toggle queues instead of
    // racing a stale version — see rowMutationScope. Paired with the Layer 0 onSuccess
    // writeback below (the endpoint now returns the refund's freshly-bumped concurrencyVersion),
    // this is the full self-race fix: serialization queues the follow-up, and the writeback
    // delivers the fresh token during rapid interaction (the next onMutate cancelQueries kills
    // the onSettled refetch, so it can't be the source).
    scope: rowMutationScope(options?.scopeId),
    mutationFn: async ({
      refundId,
      concurrencyVersion,
    }: {
      refundId: string;
      concurrencyVersion: RowVersion;
    }) => {
      const response = await apiResponse(
        `/api/finance/contribution-refunds/${refundId}/revoke-standalone`,
        { method: 'POST', json: { concurrencyVersion } satisfies UncheckRefundRequest },
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new ContributionRefundLinkError(
          body.code ?? '',
          response.status,
          body.error ?? 'Failed to uncheck refund',
        );
      }
      return readRefundMutationResponse(response);
    },
    // Uncheck instantly (Done/Standalone → NeedsAction/MatchRefund), back to orphan.
    ...transientRetry,
    onMutate: async ({ refundId }) => {
      const previous = await optimisticallyPatchSortInboxRow(
        queryClient,
        (item) => item.kind === 'Refund' && item.refund.id === refundId,
        markRefundOrphan,
      );
      return { previous };
    },
    onError: (_err, _variables, context) => {
      rollbackSortInbox(queryClient, context?.previous);
    },
    // Layer 0 — write the server's freshly-bumped token into the cached Refund row NOW, so a
    // rapid re-toggle sends the current version, not the stale one it revoked with. The
    // onSettled refetch can't be the source during rapid interaction (the next action's
    // onMutate cancelQueries kills it).
    onSuccess: (data, { refundId }) => {
      reconcileSortInboxRowVersion(
        queryClient,
        (item) => item.kind === 'Refund' && item.refund.id === refundId,
        data.concurrencyVersion,
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.sortInboxAll() });
    },
  });
}

/**
 * 2026-06-11 triage-ledger inline edit — PATCH Amount/Reason on a
 * not-yet-filed refund. Invalidates the sort-inbox so the edited row
 * re-renders with fresh values.
 */
export function useEditContributionRefund() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      refundId,
      data,
    }: {
      refundId: string;
      data: Pick<EditContributionRefundRequest, 'concurrencyVersion'> & Partial<EditContributionRefundRequest>;
    }) =>
      apiJson<EditContributionRefundResponse>(`/api/finance/contribution-refunds/${refundId}`, {
        method: 'PATCH',
        json: { ...data, amount: data.amount ?? null, reason: data.reason ?? null, date: data.date ?? null },
        error: (body, response) =>
          new ContributionRefundLinkError(
            body.code ?? '',
            response.status,
            body.error ?? 'Failed to edit refund',
          ),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.sortInboxAll() });
    },
  });
}

/**
 * ADR-016 single-row Delete — DELETE /api/finance/contribution-refunds/{id}.
 * Refunds are Delete-only in v1 (no Void / un-void): a wrong refund "never
 * happened". Distinct from the ✓-uncheck unlink/revoke flow above — Delete
 * removes the refund row outright. 204 No Content; the concurrency token rides
 * as a query param (DELETE carries no body). Refunds have no dedicated query
 * key, so this reuses the contribution + earliest-staged-date + contact +
 * sort-inbox set (deleting a refund can drop a Confirmed refund-expenditure,
 * which shifts the earliest staged date).
 */
export function useDeleteContributionRefund() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      refundId,
      concurrencyVersion,
    }: {
      refundId: string;
      concurrencyVersion: RowVersion;
    }) => {
      const response = await apiResponse(
        `/api/finance/contribution-refunds/${refundId}?concurrencyVersion=${concurrencyVersion}`,
        { method: 'DELETE' },
      );
      if (!response.ok) {
        // Delete failures carry the DeleteErrorCodes family, NOT the
        // link/standalone family — so surface the same StagedRowUpdateError the
        // contribution/expenditure delete hooks throw (consistent across all
        // three Delete hooks), not ContributionRefundLinkError.
        await throwStagedRowError(response, 'Failed to delete refund');
      }
      // 204 No Content — no payload.
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.contributions.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.earliestStagedDate() });
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.contacts.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.sortInboxAll() });
    },
  });
}
